/*
 * <cdx-read-more> – "Mehr / Weniger" toggle for long text blocks.
 *
 *   <cdx-read-more overflowing data-label-more="Mehr" data-label-less="Weniger" …>
 *     <div data-read-more-content id="x">…</div>
 *     <button type="button" data-read-more-toggle aria-controls="x" aria-expanded="false">
 *       <span data-read-more-label>Mehr</span>
 *     </button>
 *   </cdx-read-more>
 *
 * Whether the text is long enough for a toggle comes from the `overflowing` attribute (set in Liquid
 * when it depends on the text length) or, with data-max-height="400", from measuring the content –
 * re-measured on resize, so content inside a closed tab/accordion works once it becomes visible.
 *
 * State: `expanded` / `overflowing` attributes on the host, aria-expanded on the toggle, toggle
 * hidden while not overflowing. Optional class mirrors for existing CSS:
 *   data-expanded-class / data-collapsed-class (content), data-toggle-expanded-class (toggle),
 *   data-short-class (host, while not overflowing).
 * Collapsing scrolls back to data-scroll-to (closest ancestor or selector), with
 * data-scroll-offset (px, default 20) and data-scroll-header="false" to ignore the sticky header.
 */
if (!customElements.get("cdx-read-more")) {
  customElements.define(
    "cdx-read-more",
    class CdxReadMore extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector("[data-read-more-content]");
        this.toggle = this.querySelector("[data-read-more-toggle]");
        this.label = this.querySelector("[data-read-more-label]");
        if (!this.content || !this.toggle) return;

        this.abortController = new AbortController();
        this.toggle.addEventListener("click", () => this.setExpanded(!this.hasAttribute("expanded")), {
          signal: this.abortController.signal,
        });

        if (this.dataset.maxHeight) {
          // Renders once measured (the observer fires right away); until then the server markup stays
          this.resizeObserver = new ResizeObserver(() => this.measure());
          this.resizeObserver.observe(this.content);
        } else {
          this.render();
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
      }

      measure() {
        // scrollHeight is the full height even while max-height clips the content;
        // 0 means it is hidden (closed tab) – wait for the next resize instead.
        const height = this.content.scrollHeight;
        if (!height) return;
        this.toggleAttribute("overflowing", height > Number(this.dataset.maxHeight));
        this.render();
      }

      setExpanded(expanded) {
        this.toggleAttribute("expanded", expanded);
        this.render();
        if (!expanded) this.scrollBack();
      }

      render() {
        const { dataset } = this;
        const overflowing = this.hasAttribute("overflowing");
        const expanded = overflowing && this.hasAttribute("expanded");

        this.toggle.hidden = !overflowing;
        this.toggle.setAttribute("aria-expanded", String(expanded));
        if (this.label) {
          this.label.textContent = expanded ? dataset.labelLess : dataset.labelMore;
        }
        if (dataset.expandedClass) this.content.classList.toggle(dataset.expandedClass, expanded);
        if (dataset.collapsedClass) {
          this.content.classList.toggle(dataset.collapsedClass, overflowing && !expanded);
        }
        if (dataset.toggleExpandedClass) {
          this.toggle.classList.toggle(dataset.toggleExpandedClass, expanded);
        }
        if (dataset.shortClass) this.classList.toggle(dataset.shortClass, !overflowing);
      }

      scrollBack() {
        const selector = this.dataset.scrollTo;
        if (!selector || !window.CdxUtils) return;
        const target = this.closest(selector) || document.querySelector(selector);
        window.CdxUtils.scrollToElementWithOffset(target, {
          extra: Number(this.dataset.scrollOffset ?? 20),
          includeHeader: this.dataset.scrollHeader !== "false",
        });
      }
    }
  );
}
